import React, { useEffect, useState } from "react";
import { css } from "@emotion/react";
import { stringify } from "qs";
import { decamelizeKeys } from "humps";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { vesselCabinRatesSelector } from "../cruiseSlice";
import { useAppSelector } from "main/javascripts/store";
import { space } from "main/javascripts/styles/base/spaceStyle";
import {
  cruiseShowParamSelector,
  isTrackingParameterStoredSelector,
} from "../../../slices/paramSlice";
import { isPhone } from "main/javascripts/styles/base/responsiveStyle";
import {
  fontSize,
  fontWeight,
  lineHeight,
} from "main/javascripts/styles/base/typographyStyle";
import {
  accentColor,
  backgroundColor,
  borderColor,
  shadeColor,
  textColor,
} from "main/javascripts/styles/base/colorStyle";
import { isFetchedAccountParamSelector } from "main/javascripts/slices/accountParamSlice";
import { LINE_ENTRY_LIFF_URL } from "main/javascripts/constants/Constants";
import { getLineEntryValues } from "main/javascripts/utils/trackingUtil";
import { nl2br } from "main/javascripts/utils/StringUtil";
import { borderRadius } from "main/javascripts/styles/base/borderStyle";
import { GoogleTagManagerEventTypes } from "main/javascripts/constants/GoogleTagManagerEventTypes";
import { isMobile } from "main/javascripts/utils/DeviceUtil";

export const CruiseCabinFooterCard: React.FC = (): React.ReactElement => {
  const cabinRates = useAppSelector(vesselCabinRatesSelector);
  const param = useAppSelector(cruiseShowParamSelector);
  // TODO: 旧reducerをそのまま使用
  const breakpoint: any = useSelector<any>(
    (state) => state.breakpointReducer.breakpoint
  );

  const { t } = useTranslation([
    "component",
    "label",
    "unit",
    "button",
    "constant",
  ]);

  const isTrackingParameterStored = useAppSelector(
    isTrackingParameterStoredSelector
  );
  const isFetchedAccountParam = useAppSelector(isFetchedAccountParamSelector);

  const [showCard, setShowCard] = useState(false);
  const [showLineButton, setShowLineButton] = useState(false);
  const [lineEntryUrl, setLineEntryUrl] = useState(LINE_ENTRY_LIFF_URL);
  const [emailConsultationPath, setEmailConsultationPath] = useState(
    `/mail_cruise_consultations/new`
  );

  const cabinRate = param.cabinCode
    ? cabinRates.find((cabinRate) => cabinRate.cabinCode === param.cabinCode)
    : [...cabinRates].sort((a, b) => a.priceCents - b.priceCents)[0];

  const cabin = cabinRate?.vesselCabin;
  const rate = cabinRate?.rate;
  const guestNum = rate?.adults + rate?.children || 0;

  useEffect(() => {
    setShowLineButton(isMobile());
  }, []);

  useEffect(() => {
    setShowCard(!!rate);
  }, [rate]);

  useEffect(() => {
    if (cabinRate && (isTrackingParameterStored || isFetchedAccountParam)) {
      const lineEntryValues = getLineEntryValues();
      const values = decamelizeKeys(param);
      const query: { [key: string]: any } = {
        cruise_id: cabinRate.cruiseUniqueId,
        vessel_cabin_code: cabinRate.cabinCode,
        departure_date: values.departure_date,
        guests: values.guests,
        ...lineEntryValues,
      };
      const queryString = stringify(query);
      setLineEntryUrl(`${LINE_ENTRY_LIFF_URL}?${queryString}`);
      const emalConsultationBasePath = `/mail_cruise_consultations/${cabinRate.cruiseUniqueId}/new`;
      setEmailConsultationPath(`${emalConsultationBasePath}?${queryString}`);
    }
  }, [isTrackingParameterStored, isFetchedAccountParam]);

  if (!isPhone(breakpoint)) {
    return null;
  }

  const priceBlockStyles = showLineButton
    ? priceBlockStyle
    : priceBlockOneButtonStyle;
  const emailButtonStyles = showLineButton ? buttonStyle : buttonLargeStyle;

  return (
    <div css={[blockStyle, showCard && showStyle]}>
      <div css={infoBlockStyle}>
        {rate ? (
          <div css={priceBlockStyles}>
            <div css={priceStyle}>
              <div css={titleStyle}>{cabin.name}</div>
              {rate.price}
              <span css={priceNoteStyle}>
                {t("unit:person", { count: guestNum })} /{" "}
                {t("cruise.includeTaxAndFee")}
              </span>
            </div>
            <div css={buttonBlockStyle}>
              <a
                href={emailConsultationPath}
                css={emailButtonStyles}
                data-event-category={
                  GoogleTagManagerEventTypes.clickCruiseConsultation
                }
              >
                {showLineButton
                  ? nl2br(t("button:common.contactViaEmailMultiline"))
                  : t("button:common.contactViaEmail")}
              </a>
              {showLineButton && (
                <a
                  href={lineEntryUrl}
                  css={buttonLineStyle}
                  data-event-category={
                    GoogleTagManagerEventTypes.clickCruiseConsultation
                  }
                >
                  {nl2br(t("button:common.contactViaLineMultiline"))}
                </a>
              )}
            </div>
          </div>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};

const blockStyle = css`
  position: fixed;
  z-index: 1;
  bottom: 0;
  left: 0;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  gap: ${space.atom2x};
  background-color: ${backgroundColor.primaryLightColor};
  border-top: 1px solid ${borderColor.secondaryLightColor};
  box-shadow: 0 -3px 12px rgba(0, 0, 0, 0.06);
  transform: translateY(101%);
  pointer-events: none;
  height: 78px;
`;
const showStyle = css`
  transform: translateY(0);
  pointer-events: auto;
  transition: transform 0.4s ease;
`;
const infoBlockStyle = css`
  width: 100%;
  box-sizing: border-box;
  padding: 0 ${space.atom0_5x};
`;
const titleStyle = css`
  font-size: ${fontSize.caption};
  line-height: ${lineHeight.caption2};
  font-weight: ${fontWeight.regular};
  color: ${textColor.primaryDarkColor};
  padding-bottom: 0.125rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const priceBlockStyle = css`
  display: grid;
  grid-template-columns: 1fr minmax(50%, 10rem);
  align-items: center;
  justify-content: space-between;
  gap: ${space.atom} ${space.atom};
  padding: ${space.atom} ${space.atom};
`;
const priceBlockOneButtonStyle = css`
  ${priceBlockStyle};
  grid-template-columns: 1fr minmax(40%, 10rem);
`;
const priceStyle = css`
  flex: 1;
  min-width: 0;
  color: ${textColor.primaryDarkColor};
  font-size: ${fontSize.mediumHeading};
  font-weight: ${fontWeight.bold};
  font-variant-numeric: tabular-nums;
  line-height: ${lineHeight.noSpace};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const priceNoteStyle = css`
  display: block;
  font-weight: ${fontWeight.regular};
  font-size: ${fontSize.smallest};
  line-height: ${lineHeight.caption3};
  color: ${textColor.secondaryDarkColor};
  opacity: 0.7;
  margin-top: ${space.atom0_5x};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const buttonBlockStyle = css`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: ${space.atom};
`;
const buttonStyle = css`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  box-sizing: border-box;
  overflow: hidden;
  outline: none;
  cursor: pointer;
  text-decoration: none;
  border-radius: ${borderRadius.normal};
  margin: 0;
  width: 100%;
  transition: background 0.4s ease, opacity 0.4s ease;
  font-weight: ${fontWeight.medium};
  color: white;
  background-color: ${accentColor.primaryColor};
  padding: 0;
  border: none;
  min-width: 64px;
  height: 40px;
  font-size: 0.75rem;
  line-height: 1.2;
  text-align: center;
  &:hover {
    background-color: ${accentColor.primaryDarkColor};
  }
`;
const buttonLineStyle = css`
  ${buttonStyle};
  background-color: ${accentColor.lineColor};
  &:hover {
    background-color: ${shadeColor(accentColor.lineColor, -5)};
  }
`;
const buttonLargeStyle = css`
  ${buttonStyle};
  font-size: ${fontSize.caption};
`;
